import {
  Component,
  effect,
  EventEmitter,
  inject,
  model,
  ModelSignal,
  Output,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import {EControlActions, EsvgFiles, IControlDataChanged, INITIAL_STATE_TOKEN,} from 'frontier/nucleus';
import {combineLatest, debounceTime, Observable, of,} from "rxjs";
import {
  CSV_EXPORT_ACTION,
  ITableAction,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
  TabsService,
  ToolbarStore,
} from 'frontier/browserkit';
import {GridOptions, RowNode} from "ag-grid-community";
import {filter, finalize, tap} from "rxjs/operators";
import {IPdfOverlayToggleEvent} from '../carts/pdf-overlay-toggle-event.interface';
import {ECalculusStateFilter} from '../../../config/state-filter.enum';
import {toObservable} from '@angular/core/rxjs-interop';
import {ProcessOverviewStore} from '../service/process-overview.store';
import {OpenItemListService} from 'src/app/api/generated';
import AppControlGuids from '../../../../api/ControlGuids';
import {IClient, IInvoice, IPdfOverlay, IProcessState} from '../service/state.interface';
import {LetterOfResignationService} from '../../letter-of-resignation/letter-of-resignation.service';

@Component({
  selector: 'kpi4me-open-items',
  templateUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: OpenItemsComponent},
  ],
})
export class OpenItemsComponent extends TableControlComponent {
  private _toolbarStore: ToolbarStore = inject(ToolbarStore);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _openItemListService: OpenItemListService = inject(OpenItemListService);
  private _tabService: TabsService = inject(TabsService);
  private _letterOfResignationService = inject(LetterOfResignationService);

  selectedProcess: Signal<IProcessState> = this._processOverviewService.selectedProcess;
  selectedProcess$: Observable<IProcessState> = toObservable(this.selectedProcess);
  selectedClient: Signal<IClient> = this._processOverviewService.selectedClient;
  selectedClient$: Observable<IClient> = toObservable(this.selectedClient);
  selectedInvoice: Signal<IInvoice> = this._processOverviewService.selectedInvoice;
  selectedInvoice$: Observable<IInvoice> = toObservable(this.selectedInvoice);

  pdfOverlay: Signal<IPdfOverlay> = this._processOverviewService.pdfOverlay;
  isPdfLoading: Signal<boolean> = this._processOverviewService.isPdfLoading;

  @Output() invoiceToggleTriggered: EventEmitter<IPdfOverlayToggleEvent> = new EventEmitter<IPdfOverlayToggleEvent>();

  override GUID: string = AppControlGuids.TOpenItemList;
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: (): string => {
        return this.pdfOverlay()?.type === 'collectiveInvoice' ? 'Rechnung ausblenden' : 'Rechnung anzeigen'
      },
      controlAction: EControlActions.invoicePreview,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.search,
      action: () => this.toggleCollectiveInvoicePreview(),
      isDisabled: (): boolean => {
        return this.isPdfLoading() === true;
      },
    },
    {
      displayName: 'Absetzungsschreiben öffnen',
      controlAction: EControlActions.load,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.link,
      action: () => this.openRevisorLink(),
    },
    {
      displayName: 'Mahnungen erstellen',
      controlAction: EControlActions.dunning,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.breaking_news,
      action: () => this.createDunning(),
      isHidden: () => {
        return !(this.selectedProcess()?.state === ECalculusStateFilter.monitorPaymentError);
      },
    },
    CSV_EXPORT_ACTION
  ]);

  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    pagination: true,
    rowSelection: 'single',
  };

  constructor() {
    super();
    effect(() => {
      // restore selection after refresh
      const selectedInvoice: IInvoice = this.selectedInvoice();
      this.savedSelectedRowsIds = selectedInvoice?.obj ? [selectedInvoice.obj.signature] : [];
    });

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged) => {
          return new Map([
            [AppControlGuids.TInvoiceList,
              new Set([EControlActions.cancel, EControlActions.uncollectible, EControlActions.payed, EControlActions.credit, EControlActions.reroll])],
            [AppControlGuids.TCartList, new Set([EControlActions.dunning])],
            [AppControlGuids.TOpenItemList, new Set([EControlActions.dunning])],
            [AppControlGuids.TCartItemsList, new Set([EControlActions.partlyCancel])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap(() => {
          // The open items table should change and fetch if an invoice was canceled.
          this.refreshInfiniteCache()
        })).subscribe(),
    );

    this.subs.add(
      this.rowSelected.pipe(
        tap((selectionEvent: RowNode[]) => {
          if (!selectionEvent[0]?.data?.apiRow) {
            return;
          }
          this._processOverviewService.selectedInvoice$.next({
            obj: selectionEvent[0].data.apiRow.obj,
            invoiceId: selectionEvent[0].data[0]?.value
          })
        })
      ).subscribe()
    );

  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.subs.add(
      combineLatest([
        this._toolbarStore.toolbarFilterDto$,
        this.selectedProcess$,
        this.selectedInvoice$,
        this.selectedClient$
      ]).pipe(
        debounceTime(50),
        tap(([toolbarFilter, selectedProcess,
               selectedInvoice, selectedClient]) => {
          const {state, category} = selectedProcess;
          // Update the apiInstance and trigger the fetching of the data with the new filter.
          this.patchFilter({
            ...toolbarFilter,
            valuelist: typeof (state) === 'number' ? [state] : category ? [category] : undefined,
            typename: selectedInvoice?.invoiceId || (selectedClient ? 'client' : null),
            parent: selectedClient?.obj || null,
          });
        }),
      ).subscribe()
    );
  }

  private toggleCollectiveInvoicePreview(): Observable<any> {
    this.invoiceToggleTriggered.emit({
      type: 'collectiveInvoice',
      data: {obj: this.gridApi?.getSelectedRows()[0].apiRow.obj, apiInstance: this.apiInstance()}
    });
    return of(null);
  }

  private openRevisorLink(): Observable<string> {
    return this._openItemListService.openItemListRevisorUrl({
      openitem: this.selectedRows()[0].apiRow.obj
    }).pipe(
      tap((link: string) => {
        this._letterOfResignationService.url.set(link)
        this._tabService.openTab('Absetzungsschreiben', 'LetterOfResignationComponent', 'letter-of-resignation');
      }),
      finalize(() => {
        this.gridApi?.hideOverlay();
      })
    );
  }

  createDunning(): Observable<boolean> {
    return this._openItemListService.openItemListCreateAllDunning({
      InstanceId: this.instanceId()
    });
  }
}
